import type { ChatModelAdapter, ChatModelRunResult } from "@assistant-ui/react";

import store from "store";
import { createTempApiKey } from "libs/api/methods/api-keys-methods";
import api from "libs/api";
import { API_ASSISTANT } from "libs/api/endpoints";

// ============================================================================
// CONFIGURATION
// ============================================================================
// The adapter uses the internal OpsHelm API wrapper at /v1/assistant
// which handles Anthropic API calls and MCP integration server-side
// ============================================================================

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Fetches a temporary MCP authentication token from the backend.
 * This token is used to authenticate with the MCP server and expires after 1 hour.
 */
async function fetchMCPAuthToken(): Promise<string | null> {
  try {
    const organizationId = store.auth.organization?.id;
    if (!organizationId) {
      // No organization available, MCP features won't be available
      return null;
    }

    const response = await createTempApiKey(organizationId);
    return response.key;
  } catch {
    // Failed to fetch token, MCP features won't be available
    return null;
  }
}

/**
 * Convert assistant-ui messages to a simple string format
 * The backend expects the latest user message
 */
function convertMessagesToString(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  messages: readonly any[],
): string {
  // Get the last user message
  const lastUserMessage = messages.filter((m) => m.role === "user").slice(-1)[0];

  if (!lastUserMessage) {
    return "";
  }

  const { content } = lastUserMessage;

  // Handle array content (with text blocks, tool calls, etc.)
  if (Array.isArray(content)) {
    return content
      .filter((c: { type: string }) => c.type === "text")
      .map((c: { text: string }) => c.text)
      .join("\n");
  }

  // Handle string content
  return typeof content === "string" ? content : "";
}

// ============================================================================
// Anthropic Adapter with Internal API
// ============================================================================

/**
 * Anthropic ChatModelAdapter using OpsHelm's internal API wrapper.
 *
 * This adapter connects to the internal /v1/assistant endpoint which
 * proxies requests to Anthropic's API and handles MCP integration server-side.
 *
 * Features:
 * - Streaming responses via axios with onDownloadProgress
 * - MCP connector integration for tool calling (handled server-side)
 * - Abort signal support for cancellation
 * - Error handling with user-friendly messages
 *
 * @see https://www.assistant-ui.com/docs/runtimes/custom/local
 */
export const anthropicAdapter: ChatModelAdapter = {
  async *run({ messages, abortSignal }) {
    // Get the last user message
    const message = convertMessagesToString(messages);

    if (!message) {
      yield {
        content: [
          {
            type: "text",
            text: "No message provided.",
          },
        ],
      } as ChatModelRunResult;
      return;
    }

    // Fetch temporary MCP token
    const mcpToken = await fetchMCPAuthToken();
    if (!mcpToken) {
      yield {
        content: [
          {
            type: "text",
            text: "Unable to authenticate. Please ensure you're logged in with an active organization.",
          },
        ],
      } as ChatModelRunResult;
      return;
    }

    try {
      let processedLength = 0;
      let buffer = "";
      let accumulatedText = "";
      const updates: string[] = [];
      let requestComplete = false;
      let requestError: Error | null = null;

      // Start the streaming request (don't await - we'll poll for updates)
      api
        .post(
          API_ASSISTANT,
          {
            message,
            stream: true,
            mcp_token: mcpToken,
          },
          {
            signal: abortSignal,
            timeout: 120000, // 2 minutes - assistant requests can take longer
            responseType: "text",
            doNotShowErrorMessage: true, // We handle errors ourselves, don't show global error
            onDownloadProgress: (progressEvent) => {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              const fullText = (progressEvent.event as any)?.target?.responseText || "";
              const newChunk = fullText.substring(processedLength);
              processedLength = fullText.length;

              buffer += newChunk;

              // Split by SSE delimiter
              const parts = buffer.split("\n\n");
              buffer = parts.pop() || ""; // Keep incomplete part

              for (const part of parts) {
                const lines = part.split("\n");
                for (const line of lines) {
                  if (line.startsWith("data: ")) {
                    const data = line.slice(6).trim();
                    try {
                      const parsed = JSON.parse(data);

                      // Handle different event types
                      if (parsed.type === "text_delta" && parsed.text) {
                        accumulatedText += parsed.text;
                        updates.push(accumulatedText);
                      } else if (parsed.type === "error" && parsed.error) {
                        // Don't treat cancellation as an error
                        const errorMsg = parsed.error.toLowerCase();
                        if (!errorMsg.includes("cancel") && !errorMsg.includes("abort")) {
                          requestError = new Error(parsed.error);
                        }
                        requestComplete = true;
                      }
                    } catch (e) {
                      // Ignore parsing errors for non-JSON data lines
                    }
                  }
                }
              }
            },
          },
        )
        .then(() => {
          requestComplete = true;
        })
        .catch((error) => {
          requestError = error;
          requestComplete = true;
        });

      // Yield updates as they come in
      let lastUpdateIndex = 0;
      while (!requestComplete || lastUpdateIndex < updates.length) {
        // Wait a bit for new updates
        // eslint-disable-next-line no-await-in-loop
        await new Promise((resolve) => {
          setTimeout(resolve, 50);
        });

        // Yield any new updates
        while (lastUpdateIndex < updates.length) {
          yield {
            content: [{ type: "text", text: updates[lastUpdateIndex] }],
          } as ChatModelRunResult;
          lastUpdateIndex += 1;
        }
      }

      // Check for errors
      if (requestError) {
        throw requestError;
      }

      // Final yield if we have text but never yielded
      if (accumulatedText && accumulatedText.trim().length > 0 && lastUpdateIndex === 0) {
        yield {
          content: [{ type: "text", text: accumulatedText }],
        } as ChatModelRunResult;
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Unknown error occurred";
      yield {
        content: [
          {
            type: "text",
            text: `Error: ${errorMessage}. Please try again.`,
          },
        ],
      } as ChatModelRunResult;
    }
  },
};

export default anthropicAdapter;
